import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import QrScanner from 'qr-scanner';
import { API_BASE_URL } from '../config/api';

const SecurityDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [currentVisitors, setCurrentVisitors] = useState([]);
  const [scannerModal, setScannerModal] = useState({ open: false, action: null });
  const [qrInput, setQrInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [stream, setStream] = useState(null);
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [hasCameraPermission, setHasCameraPermission] = useState(null);
  const [showNewScanButton, setShowNewScanButton] = useState(false);

  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);
  const scanTimeoutRef = useRef(null);
  const lastScannedRef = useRef(null);
  const scanCooldownRef = useRef(null);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);

    // Check if user is logged in before fetching data
    const token = localStorage.getItem('token');
    const staff = localStorage.getItem('staff');
    
    if (!token || !staff) {
      window.location.href = '/login';
      return;
    }

    // Delay initial fetch to allow component to mount
    setTimeout(() => {
      fetchAllData();
    }, 1000);
    
    const interval = setInterval(fetchAllData, 10000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', handleResize);
      stopScanning();
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResize = () => setWidth(window.innerWidth);

  /*
    Consolidated data fetch - calls the bulk-status once and updates all derived state
  */
  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/api/ticket-verification/bulk-status`,
        {
          date: new Date().toISOString().split('T')[0],
          tenantId: 'sevenwonders'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data || {};
      setStats(data.stats || {});

      // Activities
      const activities = [];
      (data.bookings || []).slice(0, 50).forEach(booking => {
        if (booking.activityLog) {
          booking.activityLog.forEach(log => {
            activities.push({ ...log, bookingId: booking.bookingId, customerName: booking.customerDetails?.name });
          });
        }
      });
      activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setRecentActivity(activities.slice(0, 8));

      // Current visitors inside
      const inside = (data.bookings || []).filter(b => b.entryTime && !b.exitTime) || [];
      setCurrentVisitors(inside.slice(0, 10));
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 401) {
        // Only redirect after multiple failed attempts to avoid immediate redirects
        const failedAttempts = parseInt(localStorage.getItem('failedAttempts') || '0') + 1;
        localStorage.setItem('failedAttempts', failedAttempts.toString());
        
        if (failedAttempts >= 3) {
          // Token expired or invalid - redirect to login after 3 failed attempts
          localStorage.removeItem('token');
          localStorage.removeItem('staff');
          localStorage.removeItem('failedAttempts');
          window.location.href = '/login';
        }
      } else {
        // Reset failed attempts on successful request
        localStorage.removeItem('failedAttempts');
      }
    }
  };

  const handleScanAuto = async (qrData) => {
    if (!qrData || !scannerModal.action) return;

    // Prevent duplicate scans with debounce
    if (lastScannedRef.current === qrData || scanCooldownRef.current) return;
    
    lastScannedRef.current = qrData;
    scanCooldownRef.current = setTimeout(() => {
      scanCooldownRef.current = null;
    }, 2000); // 2 second cooldown

    setScanning(true);
    setResult(null);
    stopScanning();

    try {
      const token = localStorage.getItem('token');
      const staff = JSON.parse(localStorage.getItem('staff') || '{}');
      const staffId = staff.id || localStorage.getItem('userId');

      const response = await axios.post(
        `${API_BASE_URL}/api/ticket-verification/${scannerModal.action}`,
        {
          qrData,
          staffId,
          location: 'Security Gate'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
      setShowNewScanButton(true);
      setTimeout(fetchAllData, 1000);
    } catch (error) {
      setResult({ success: false, message: error.response?.data?.message || 'Verification failed' });
      setShowNewScanButton(true);
      console.error('Verification error:', error);
    } finally {
      setScanning(false);
    }
  };

  const startCamera = async () => {
    console.log('🎥 Starting camera...');
    setHasCameraPermission(null);
    setCameraActive(true);
    
    // Wait for video element to be available with retry logic
    const waitForVideo = (attempts = 0) => {
      console.log(`🎥 Video element check attempt ${attempts + 1}:`, !!videoRef.current);
      
      if (videoRef.current) {
        console.log('✅ Video element found, starting scanner');
        setHasCameraPermission(true);
        startScanning().catch(error => {
          console.error('❌ Camera/Scanner error:', error);
          setHasCameraPermission(false);
          setCameraActive(false);
        });
      } else if (attempts < 10) {
        setTimeout(() => waitForVideo(attempts + 1), 200);
      } else {
        console.error('❌ Video element not found after 10 attempts');
        setCameraActive(false);
      }
    };
    
    waitForVideo();
  };

  const stopCamera = () => {
    stopScanning();
    setCameraActive(false);
    setHasCameraPermission(null);
  };

  const openScanner = (action) => {
    setScannerModal({ open: true, action });
    setResult(null);
    setQrInput('');
    setCameraActive(false);
    // Longer timeout to ensure modal and video element are rendered
    setTimeout(() => startCamera(), 500);
  };

  const closeScanner = () => {
    setScannerModal({ open: false, action: null });
    stopScanning();
    stopCamera();
    setResult(null);
    setQrInput('');
    setShowNewScanButton(false);
    lastScannedRef.current = null;
    if (scanCooldownRef.current) {
      clearTimeout(scanCooldownRef.current);
      scanCooldownRef.current = null;
    }
  };

  const handleNewScan = () => {
    setResult(null);
    setShowNewScanButton(false);
    lastScannedRef.current = null;
    if (scanCooldownRef.current) {
      clearTimeout(scanCooldownRef.current);
      scanCooldownRef.current = null;
    }
    startCamera();
  };

  const startScanning = async () => {
    console.log('📱 Starting QR scanning...');
    if (!videoRef.current) {
      console.error('❌ Video ref not available for scanning');
      return;
    }
    
    try {
      console.log('🔍 Requesting camera access...');
      // Check camera permissions first
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      console.log('✅ Camera stream obtained:', stream.getTracks().length, 'tracks');
      
      // Set video source
      videoRef.current.srcObject = stream;
      setStream(stream);
      
      console.log('📺 Video source set, waiting for video to load...');
      
      // Wait for video to be ready
      await new Promise((resolve) => {
        const checkVideo = () => {
          if (videoRef.current && videoRef.current.videoWidth > 0) {
            console.log('✅ Video loaded:', videoRef.current.videoWidth + 'x' + videoRef.current.videoHeight);
            resolve();
          } else {
            console.log('⏳ Waiting for video dimensions...');
            setTimeout(checkVideo, 100);
          }
        };
        checkVideo();
      });
      
      console.log('🔧 Creating QR Scanner...');
      const qrScanner = new QrScanner(
        videoRef.current,
        (result) => {
          console.log('🎯 QR Code detected:', result.data);
          if (lastScannedRef.current !== result.data && !scanCooldownRef.current) {
            lastScannedRef.current = result.data;
            scanCooldownRef.current = setTimeout(() => {
              scanCooldownRef.current = null;
            }, 2000);
            handleScanAuto(result.data);
          }
        },
        {
          highlightScanRegion: false,
          highlightCodeOutline: true,
          maxScansPerSecond: 3
        }
      );
      
      qrScannerRef.current = qrScanner;
      console.log('🚀 Starting QR Scanner...');
      await qrScanner.start();
      console.log('✅ QR Scanner started successfully');
      
      // Safety timeout
      scanTimeoutRef.current = setTimeout(() => {
        console.log('⏰ Scanner timeout reached');
        stopScanning();
      }, 60000);
    } catch (error) {
      console.error('❌ QR Scanner error:', error.name, error.message);
      if (error.name === 'NotAllowedError') {
        console.error('🚫 Camera permission denied');
      } else if (error.name === 'NotFoundError') {
        console.error('📷 No camera found');
      }
      setHasCameraPermission(false);
    }
  };

  const stopScanning = () => {
    console.log('🛑 Stopping scanner...');
    if (qrScannerRef.current) {
      console.log('🔧 Destroying QR Scanner');
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    if (stream) {
      console.log('📹 Stopping camera stream');
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      console.log('📺 Clearing video source');
      videoRef.current.srcObject = null;
    }
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }
    console.log('✅ Scanner stopped');
  };

  const generateTestQR = () => {
    const testQR = JSON.stringify({
      bookingId: 'BK' + Date.now(),
      tenantId: 'sevenwonders',
      ticketType: 'Entry Ticket',
      visitDate: new Date().toISOString(),
      customerName: 'Test Visitor'
    });
    setQrInput(testQR);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('staff');
    localStorage.removeItem('staffRole');
    localStorage.removeItem('failedAttempts');
    window.location.href = '/login';
  };

  return (
    <>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 50%, #581c87 100%)',
          padding: width <= 768 ? '12px' : '20px',
          position: 'relative'
        }}
      >


        {/* Header */}
        <div
          style={{
            background: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: width <= 768 ? '16px' : '20px',
            padding: width <= 768 ? '16px' : '24px',
            marginBottom: width <= 768 ? '16px' : '24px',
            border: '1px solid rgba(255,255,255,0.2)'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: width <= 768 ? 'flex-start' : 'center',
              flexDirection: width <= 768 ? 'column' : 'row',
              gap: width <= 768 ? '12px' : '0'
            }}
          >
            <div>
              <h1
                style={{
                  fontSize: width <= 768 ? '1.8rem' : '2.5rem',
                  fontWeight: '900',
                  color: 'white',
                  margin: 0,
                  textShadow: '0 4px 8px rgba(0,0,0,0.3)'
                }}
              >
                🛡️ Security Control
              </h1>
              <p
                style={{
                  color: 'rgba(255,255,255,0.8)',
                  margin: '8px 0 0 0',
                  fontSize: '1.1rem'
                }}
              >
                Visitor Entry & Exit Management
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div
                style={{
                  background: 'rgba(16, 185, 129, 0.2)',
                  padding: '12px 20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(16, 185, 129, 0.3)'
                }}
              >
                <div style={{ color: '#10b981', fontWeight: '700', fontSize: '14px' }}>🟢 SYSTEM ONLINE</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>{new Date().toLocaleString()}</div>
              </div>
              <button
                onClick={handleLogout}
                style={{
                  background: 'rgba(239, 68, 68, 0.2)',
                  color: '#ef4444',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                }}
              >
                🚪 LOGOUT
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: width <= 480 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: width <= 768 ? '12px' : '16px',
            marginBottom: width <= 768 ? '16px' : '24px'
          }}
        >
          {[
            { label: 'Inside Now', value: stats?.inside || 0, color: '#8b5cf6', icon: '👥' },
            { label: 'Total Today', value: stats?.total || 0, color: '#06b6d4', icon: '🎫' },
            { label: 'Entries', value: stats?.checkedIn || 0, color: '#10b981', icon: '🟢' },
            { label: 'Exits', value: stats?.checkedOut || 0, color: '#f59e0b', icon: '🔴' }
          ].map((stat, i) => (
            <div
              key={i}
              style={{
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: width <= 768 ? '12px' : '16px',
                padding: width <= 768 ? '16px 12px' : '20px',
                border: '1px solid rgba(255,255,255,0.2)',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: width <= 768 ? '1.5rem' : '2rem', marginBottom: width <= 768 ? '6px' : '8px' }}>{stat.icon}</div>
              <div
                style={{
                  fontSize: width <= 768 ? '1.5rem' : '2rem',
                  fontWeight: '900',
                  color: stat.color,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}
              >
                {stat.value}
              </div>
              <div
                style={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: width <= 768 ? '12px' : '14px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: width <= 768 ? '16px' : '24px',
            marginBottom: width <= 768 ? '16px' : '24px'
          }}
        >
          <button
            onClick={() => openScanner('check-in')}
            style={buttonStyle(width, 'checkin')}
            onMouseOver={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
            onMouseOut={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
          >
            🟢 CHECK IN VISITOR
          </button>

          <button
            onClick={() => openScanner('check-out')}
            style={buttonStyle(width, 'checkout')}
            onMouseOver={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
            onMouseOut={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
          >
            🔴 CHECK OUT VISITOR
          </button>
        </div>

        {/* Activity & Visitors */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: width <= 768 ? '1fr' : '1fr 1fr',
            gap: width <= 768 ? '16px' : '24px'
          }}
        >
          {/* Current Visitors */}
          <div style={panelStyle(width)}>
            <h3 style={panelTitleStyle(width)}>👥 Visitors Inside ({currentVisitors.length})</h3>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {currentVisitors.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', padding: '20px' }}>No visitors currently inside</div>
              ) : (
                currentVisitors.map((visitor, i) => (
                  <div key={i} style={visitorRowStyle(width)}>
                    <div>
                      <div style={{ color: 'white', fontWeight: '600', fontSize: width <= 768 ? '13px' : '14px' }}>{visitor.customerDetails?.name}</div>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: width <= 768 ? '11px' : '12px' }}>{visitor.bookingId}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#10b981', fontSize: '12px', fontWeight: '600' }}>IN: {new Date(visitor.entryTime).toLocaleTimeString()}</div>
                      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>{Math.round((new Date() - new Date(visitor.entryTime)) / (1000 * 60))}m ago</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div style={panelStyle(width)}>
            <h3 style={panelTitleStyle(width)}>📊 Recent Activity</h3>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {recentActivity.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', padding: '20px' }}>No recent activity</div>
              ) : (
                recentActivity.map((activity, i) => (
                  <div key={i} style={activityRowStyle(width)}>
                    <div style={{ fontSize: '16px' }}>{activity.action === 'check-in' ? '🟢' : '🔴'}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: 'white', fontWeight: '600', fontSize: width <= 768 ? '13px' : '14px' }}>{activity.customerName}</div>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: width <= 768 ? '11px' : '12px' }}>{activity.action.toUpperCase()} • {activity.bookingId}</div>
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>{new Date(activity.timestamp).toLocaleTimeString()}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* QR Scanner Modal */}
        {scannerModal.open && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#000000', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
            {/* Scanner Header */}
            <div style={{ background: 'rgba(0,0,0,0.8)', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ color: 'white', fontSize: '18px', fontWeight: '700' }}>{scannerModal.action === 'check-in' ? '🟢 CHECK IN SCANNER' : '🔴 CHECK OUT SCANNER'}</div>
              <button onClick={closeScanner} style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', padding: '8px 12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>× CLOSE</button>
            </div>

            {/* Camera View - Full Screen */}
            <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
              {cameraActive ? (
                <>
                  <video 
                    ref={videoRef} 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover', 
                      background: '#000', 
                      zIndex: 1 
                    }} 
                    playsInline 
                    muted 
                    autoPlay
                    onLoadedMetadata={() => {
                      console.log('📺 Video metadata loaded');
                      if (videoRef.current) {
                        videoRef.current.play();
                      }
                    }}
                    onCanPlay={() => console.log('▶️ Video can play')}
                    onPlay={() => console.log('🎥 Video started playing')}
                    onError={(e) => console.error('❌ Video error:', e)}
                  />

                  {/* Scanner Overlay */}
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.6) 100%)', pointerEvents: 'none', zIndex: 2 }} />

                  {/* Enhanced Scanning Frame */}
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '280px', height: '280px', border: '3px solid #00ff00', borderRadius: '16px', pointerEvents: 'none', boxShadow: '0 0 30px rgba(0, 255, 0, 0.6)', animation: scanning ? 'pulse 1s infinite' : 'scannerIdle 2s ease-in-out infinite', zIndex: 3 }} />

                  {/* Corner Brackets */}
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} style={{ position: 'absolute', top: i < 2 ? 'calc(50% - 150px)' : 'calc(50% + 120px)', left: i % 2 === 0 ? 'calc(50% - 150px)' : 'calc(50% + 120px)', width: '30px', height: '30px', border: '4px solid #00ff00', borderRight: i % 2 === 0 ? 'none' : '4px solid #00ff00', borderLeft: i % 2 === 1 ? 'none' : '4px solid #00ff00', borderBottom: i < 2 ? 'none' : '4px solid #00ff00', borderTop: i >= 2 ? 'none' : '4px solid #00ff00', pointerEvents: 'none', animation: 'cornerPulse 1.5s ease-in-out infinite' }} />
                  ))}

                  {/* Scanning Line */}
                  {scanning && <div style={{ position: 'absolute', top: '50%', left: 'calc(50% - 140px)', width: '280px', height: '3px', background: 'linear-gradient(90deg, transparent, #00ff00, #00ff00, transparent)', animation: 'scanLine 2s linear infinite', pointerEvents: 'none', boxShadow: '0 0 10px #00ff00' }} />}

                  {/* Instructions */}
                  <div style={{ position: 'absolute', bottom: '120px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.9)', color: 'white', padding: '12px 24px', borderRadius: '25px', fontSize: '16px', fontWeight: '600', textAlign: 'center', border: '2px solid rgba(0, 255, 0, 0.3)', backdropFilter: 'blur(10px)' }}>📱 Position QR code within the frame</div>

                  {/* Processing Indicator */}
                  {scanning && <div style={{ position: 'absolute', top: '30%', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,255,0,0.95)', color: 'black', padding: '12px 20px', borderRadius: '25px', fontSize: '16px', fontWeight: '800', animation: 'pulse 1s infinite', boxShadow: '0 0 20px rgba(0,255,0,0.5)' }}>⏳ PROCESSING...</div>}
                  
                  {/* Camera Error Overlay */}
                  {hasCameraPermission === false && (
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'rgba(239, 68, 68, 0.95)', borderRadius: '20px', padding: '28px', maxWidth: '420px', width: '90%', textAlign: 'center', color: 'white' }}>
                      <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🚫</div>
                      <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>Camera Access Blocked</div>
                      <div style={{ fontSize: '14px', opacity: 0.8, lineHeight: '1.5' }}>
                        <p>Chrome blocks camera on localhost.</p>
                        <p><strong>To enable:</strong></p>
                        <p>1. Click 🔒 in address bar</p>
                        <p>2. Allow Camera permission</p>
                        <p>3. Refresh page</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', color: 'white', padding: '40px' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '20px', animation: 'pulse 2s infinite' }}>📷</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>Initializing Scanner...</div>
                  <div style={{ fontSize: '14px', opacity: 0.7 }}>Please wait</div>
                </div>
              )}
            </div>

            {/* Enhanced Result Display */}
            {result && (
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: result.success ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.95), rgba(5, 150, 105, 0.95))' : 'linear-gradient(135deg, rgba(239, 68, 68, 0.95), rgba(220, 38, 38, 0.95))', borderRadius: '20px', padding: '28px', maxWidth: '420px', width: '90%', border: `3px solid ${result.success ? '#10b981' : '#ef4444'}`, boxShadow: '0 25px 50px rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)' }}>
                <div style={{ textAlign: 'center', fontSize: '4rem', marginBottom: '20px', animation: 'resultPop 0.5s ease-out' }}>{result.success ? '✅' : '❌'}</div>

                <div style={{ fontWeight: '900', marginBottom: '16px', fontSize: '24px', color: 'white', textAlign: 'center', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>{result.success ? 'SUCCESS' : 'ERROR'}</div>

                <div style={{ color: 'white', marginBottom: '20px', fontSize: '18px', textAlign: 'center', fontWeight: '600', lineHeight: '1.4' }}>{result.message}</div>

                {result.data && (
                  <div style={{ background: 'rgba(255,255,255,0.25)', padding: '20px', borderRadius: '16px', fontSize: '15px', color: 'white', marginBottom: '20px', border: '1px solid rgba(255,255,255,0.2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
                      <span style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}><span>👤</span> Customer:</span>
                      <span style={{ fontWeight: '700' }}>{result.data.customerName}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
                      <span style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}><span>🎫</span> Booking:</span>
                      <span style={{ fontFamily: 'monospace', background: 'rgba(255,255,255,0.2)', padding: '4px 8px', borderRadius: '6px', fontSize: '13px' }}>{result.data.bookingId}</span>
                    </div>
                    {result.data.quantity && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
                        <span style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}><span>👥</span> Visitors:</span>
                        <span style={{ fontWeight: '700', background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '6px' }}>{result.data.adults}A + {result.data.children}C</span>
                      </div>
                    )}
                    {result.data.entryTime && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
                        <span style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}><span>🕐</span> Entry:</span>
                        <span style={{ fontSize: '13px' }}>{new Date(result.data.entryTime).toLocaleString()}</span>
                      </div>
                    )}
                    {result.data.duration && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}><span>⏱️</span> Duration:</span>
                        <span style={{ fontWeight: '700' }}>{result.data.duration}</span>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px' }}>
                  {showNewScanButton && (
                    <button onClick={handleNewScan} style={{ flex: 1, padding: '14px', background: 'rgba(255,255,255,0.2)', color: 'white', border: '2px solid rgba(255,255,255,0.5)', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', textTransform: 'uppercase', transition: 'all 0.2s' }}>🔄 NEW SCAN</button>
                  )}
                  <button onClick={closeScanner} style={{ flex: 1, padding: '14px', background: 'rgba(255,255,255,0.3)', color: 'white', border: '2px solid white', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', textTransform: 'uppercase' }}>✕ CLOSE</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        
        @keyframes scanLine {
          0% { transform: translateY(-140px); }
          100% { transform: translateY(140px); }
        }
        
        @keyframes scannerIdle {
          0%, 100% { box-shadow: 0 0 30px rgba(0, 255, 0, 0.6); }
          50% { box-shadow: 0 0 40px rgba(0, 255, 0, 0.8); }
        }
        
        @keyframes cornerPulse {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }
        
        @keyframes resultPop {
          0% { transform: scale(0.5); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </>
  );
};

function buttonStyle(width, type) {
  const base = {
    padding: width <= 768 ? '20px' : '24px',
    color: 'white',
    border: 'none',
    borderRadius: width <= 768 ? '16px' : '20px',
    fontSize: width <= 768 ? '16px' : '18px',
    fontWeight: '800',
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
    transition: 'all 0.2s ease'
  };

  if (type === 'checkin') {
    return { ...base, background: 'linear-gradient(135deg, #10b981, #059669)' };
  }
  return { ...base, background: 'linear-gradient(135deg, #f59e0b, #d97706)' };
}

function panelStyle(width) {
  return {
    background: 'rgba(255,255,255,0.1)',
    backdropFilter: 'blur(10px)',
    borderRadius: width <= 768 ? '16px' : '20px',
    padding: width <= 768 ? '16px' : '24px',
    border: '1px solid rgba(255,255,255,0.2)'
  };
}

function panelTitleStyle(width) {
  return {
    color: 'white',
    fontSize: width <= 768 ? '1.2rem' : '1.5rem',
    fontWeight: '800',
    margin: '0 0 16px 0'
  };
}

function visitorRowStyle(width) {
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: width <= 768 ? '10px 12px' : '12px 16px',
    marginBottom: '8px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.2)'
  };
}

function activityRowStyle(width) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: width <= 768 ? '8px' : '12px',
    padding: width <= 768 ? '10px 12px' : '12px 16px',
    marginBottom: '8px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.2)'
  };
}

export default SecurityDashboard;
